export type AudioContainer = 'wav' | 'webm' | 'mp4' | 'unknown'

export function getAudioContainerFromMimeType(mimeType?: string | null): AudioContainer {
  const normalized = mimeType?.toLowerCase().trim()
  if (!normalized) return 'unknown'
  if (normalized.includes('wav')) return 'wav'
  if (normalized.includes('webm')) return 'webm'
  if (normalized.includes('mp4') || normalized.includes('m4a')) return 'mp4'
  return 'unknown'
}

export function getMimeTypeForAudioContainer(container: AudioContainer): string | null {
  switch (container) {
    case 'wav':
      return 'audio/wav'
    case 'webm':
      return 'audio/webm'
    case 'mp4':
      return 'audio/mp4'
    default:
      return null
  }
}

export function detectAudioContainerFromBytes(input: ArrayBuffer | Uint8Array): AudioContainer {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input)
  if (bytes.length < 4) return 'unknown'

  const isWebM =
    bytes[0] === 0x1a && bytes[1] === 0x45 && bytes[2] === 0xdf && bytes[3] === 0xa3
  if (isWebM) {
    return 'webm'
  }

  if (bytes.length >= 12) {
    const riff = String.fromCharCode(...bytes.slice(0, 4))
    const wave = String.fromCharCode(...bytes.slice(8, 12))
    if (riff === 'RIFF' && wave === 'WAVE') {
      return 'wav'
    }

    const mp4Box = String.fromCharCode(...bytes.slice(4, 8))
    if (mp4Box === 'ftyp') {
      return 'mp4'
    }
  }

  return 'unknown'
}

export async function normalizeRecordingBlob(
  blob: Blob,
): Promise<{ blob: Blob; extension: string }> {
  const blobType = blob.type?.toLowerCase() || ''
  const needsConversion = 
    blobType.includes('webm') || 
    blobType.includes('mp4') || 
    blobType.includes('m4a') ||
    !blobType ||
    blobType === 'application/octet-stream'

  // If it's already WAV or a supported format, return as-is
  if (!needsConversion && blobType.includes('wav')) {
    return { blob, extension: 'wav' }
  }

  // Convert WebM, MP4, or unknown formats to WAV for maximum compatibility
  try {
    const arrayBuffer = await blob.arrayBuffer()
    const audioContext = new AudioContext()
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
    const wavBuffer = audioBufferToWav(audioBuffer)
    return { blob: new Blob([wavBuffer], { type: 'audio/wav' }), extension: 'wav' }
  } catch (error) {
    console.warn('Failed to convert recording to WAV format', error)
    const fallbackExtension = blob.type?.split('/')[1] ?? 'bin'
    return { blob, extension: fallbackExtension }
  }
}

export function audioBufferToWav(buffer: AudioBuffer): ArrayBuffer {
  const numChannels = buffer.numberOfChannels
  const sampleRate = buffer.sampleRate
  const numFrames = buffer.length
  const bytesPerSample = 2
  const blockAlign = numChannels * bytesPerSample
  const bufferLength = 44 + numFrames * blockAlign
  const arrayBuffer = new ArrayBuffer(bufferLength)
  const view = new DataView(arrayBuffer)

  writeString(view, 0, 'RIFF')
  view.setUint32(4, 36 + numFrames * blockAlign, true)
  writeString(view, 8, 'WAVE')
  writeString(view, 12, 'fmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true)
  view.setUint16(22, numChannels, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * blockAlign, true)
  view.setUint16(32, blockAlign, true)
  view.setUint16(34, bytesPerSample * 8, true)
  writeString(view, 36, 'data')
  view.setUint32(40, numFrames * blockAlign, true)

  let offset = 44
  for (let i = 0; i < numFrames; i++) {
    for (let channel = 0; channel < numChannels; channel++) {
      let sample = buffer.getChannelData(channel)[i]
      sample = Math.max(-1, Math.min(1, sample))
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true)
      offset += 2
    }
  }

  return arrayBuffer
}

function writeString(view: DataView, offset: number, value: string): void {
  for (let i = 0; i < value.length; i++) {
    view.setUint8(offset + i, value.charCodeAt(i))
  }
}

export function ensureAudioBlobType(blob: Blob): Blob {
  if (blob.type && blob.type.trim().length > 0) {
    return blob
  }
  return new Blob([blob], { type: 'audio/webm' })
}

export async function ensureWavBlob(blob: Blob): Promise<Blob> {
  try {
    const arrayBuffer = await blob.arrayBuffer()
    const audioContext = new AudioContext()
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
    const wavBuffer = audioBufferToWav(audioBuffer)
    return new Blob([wavBuffer], { type: 'audio/wav' })
  } catch (error) {
    console.warn('ensureWavBlob: failed to decode, falling back to WAV wrapper', error)
    return new Blob([blob], { type: 'audio/wav' })
  }
}

export async function stitchBlobsToWav(blobs: Blob[]): Promise<Blob> {
  if (blobs.length === 0) {
    return new Blob()
  }

  const audioContext = new AudioContext()
  const decodedBuffers = await Promise.all(
    blobs.map(async (chunk) => audioContext.decodeAudioData(await chunk.arrayBuffer())),
  )

  const sampleRate = decodedBuffers[0].sampleRate
  const channelCount = decodedBuffers[0].numberOfChannels
  const totalFrames = decodedBuffers.reduce((sum, buffer) => sum + buffer.length, 0)
  const stitched = audioContext.createBuffer(channelCount, totalFrames, sampleRate)

  let offset = 0
  for (const buffer of decodedBuffers) {
    for (let channel = 0; channel < channelCount; channel++) {
      stitched.getChannelData(channel).set(buffer.getChannelData(channel), offset)
    }
    offset += buffer.length
  }

  const wavBuffer = audioBufferToWav(stitched)
  return new Blob([wavBuffer], { type: 'audio/wav' })
}
